import {
  type CurrentUser,
  claimTaskMutationKey,
  type RoleName,
  releaseTaskMutationKey,
  type TaskAssignee,
  taskByIdQueryKey,
  taskEventsQueryKey,
  tasksQueryKey,
  type UserListItem,
  useAssignTaskMutation,
  useCurrentUserQuery,
  useUsersQuery,
} from '@cartwheel/api/v1'
import { PeoplePicker, type PeoplePickerPerson, useToast } from '@cartwheel/components'
import { trackError } from '@cartwheel/datadog'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

const ASSIGNABLE_ROLES_BY_QUEUE: Record<string, RoleName[]> = {
  cc: ['coordinator', 'lead', 'admin'],
  admin: ['lead', 'admin'],
  eligibility: ['rcm_specialist', 'admin'],
  clinical_supervisor: ['clinical_supervisor'],
}
const DEFAULT_ASSIGNABLE_ROLES: RoleName[] = ['coordinator']

export function TaskAssigneePicker({
  taskId,
  queue,
  assignee,
  disabled = false,
}: {
  taskId: string
  queue: string
  assignee?: TaskAssignee | null
  disabled?: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const ownershipMutationPending =
    useIsMutating({ mutationKey: claimTaskMutationKey() }) +
      useIsMutating({ mutationKey: releaseTaskMutationKey() }) >
    0
  const detailQueryKey = taskByIdQueryKey({ path: { id: taskId } })

  const assignableRoles = ASSIGNABLE_ROLES_BY_QUEUE[queue] ?? DEFAULT_ASSIGNABLE_ROLES

  const currentUserQuery = useCurrentUserQuery()
  const usersQuery = useUsersQuery({ query: { 'roles[]': assignableRoles } })
  const assignMutation = useAssignTaskMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey })
    },
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey })
      queryClient.setQueryData(detailQueryKey, data)
      void queryClient.invalidateQueries({ queryKey: tasksQueryKey() })
      void queryClient.invalidateQueries({
        queryKey: taskEventsQueryKey({ path: { task_id: taskId } }),
      })
    },
    onError: (error, variables) => {
      trackError(error, {
        context: 'task_assignee_picker.update_assignee',
        taskId,
        assigneeId: variables.body.assignee_id,
      })
      void queryClient.invalidateQueries({ queryKey: detailQueryKey })
      toast.error({ description: 'Failed to update assignee' })
    },
  })

  const people = useMemo(
    () => toPeoplePickerPeople(usersQuery.data ?? [], currentUserQuery.data?.user, assignee),
    [usersQuery.data, currentUserQuery.data?.user, assignee]
  )

  const handleAssigneeChange = (userId: string | null) => {
    const ownershipMutations =
      queryClient.isMutating({ mutationKey: claimTaskMutationKey() }) +
      queryClient.isMutating({ mutationKey: releaseTaskMutationKey() })
    if (ownershipMutations > 0) return

    assignMutation.mutate({
      path: { id: taskId },
      body: { assignee_id: userId },
    })
  }

  return (
    <div className="min-w-0">
      <PeoplePicker
        people={people}
        value={assignee?.id ?? null}
        loading={usersQuery.isPending || currentUserQuery.isPending}
        loadingMessage="Loading…"
        error={Boolean(usersQuery.error || currentUserQuery.error)}
        errorMessage="Unable to load users"
        emptyMessage="No users found"
        disabled={disabled || assignMutation.isPending || ownershipMutationPending}
        clearable
        onChange={handleAssigneeChange}
      />
    </div>
  )
}

function toPeoplePickerPeople(
  users: UserListItem[],
  currentUser?: CurrentUser,
  assignee?: TaskAssignee | null
): PeoplePickerPerson[] {
  const mappedUsers: PeoplePickerPerson[] = users.map((user) => ({
    id: user.id,
    name: user.name,
    avatarUrl: user.avatar_url,
    annotation: user.id === currentUser?.id ? 'Me' : undefined,
  }))

  if (assignee && !mappedUsers.some((user) => user.id === assignee.id)) {
    mappedUsers.push({
      id: assignee.id,
      name: assignee.name,
      avatarUrl: assignee.avatar_url,
      annotation: assignee.id === currentUser?.id ? 'Me' : undefined,
    })
  }

  if (!currentUser) return mappedUsers

  const currentUserOption = mappedUsers.find((user) => user.id === currentUser.id)
  if (!currentUserOption) return mappedUsers

  const otherUsers = mappedUsers.filter((user) => user.id !== currentUser.id)

  return [{ ...currentUserOption, dividerAfter: otherUsers.length > 0 }, ...otherUsers]
}
